import { call, ForkEffect, put, takeLatest, debounce, select } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import { IUserSlice, userActions, UserUpdateFormData } from './userSlice';
import {
  getUser,
  updateUser,
  changePassword,
  validateEmail,
  getDesignations,
  getUserList,
  createOrUpdatePin,
  UserUpdateData,
  updateUserPin,
  checkPassword,
} from '../../api/userApi';
import { authActions } from '../auth/authSlice';
import { toastStyles } from '../../utils/styles';
import { uploadImageToS3 } from '../../api/s3Api';
import { RootState } from '../store';

function* watchGetUser() {
  try {
    const res: AxiosResponse = yield call(getUser);
    yield put(
      userActions.setUser({
        userId: res.data.userId,
        email: res.data.email,
        role: res.data.role,
        username: res.data.username,
        firstName: res.data.firstName,
        familyName: res.data.familyName,
        phone: res.data.phone,
        avatar: res.data.avatar,
        permissions: res.data.permissions,
        organizationId: res.data.organizationId,
        usage: res.data.usage,
        isPinSkipped: res.data.isPinSkipped,
        tawkHush: res.data.tawkHush,
        isPaymentOverdue: res.data.isPaymentOverdue,
        companyName: res.data.companyName,
        systemId: res.data.systemId,
        isNccUser: res.data.isNccUser,
      }),
    );
  } catch (error) {
    yield put(authActions.setLogInStatus(false));
  }

  yield put(authActions.setComponentStatus({ isPageLoading: false }));
}

function* watchGetUserList() {
  try {
    yield put(userActions.setComponentStatus({ getUserList: true }));
    const res: AxiosResponse = yield call(getUserList);
    yield put(userActions.setUserList(res.data.data));
    yield put(userActions.setComponentStatus({ getUserList: false }));
  } catch (error) {
    yield put(userActions.setComponentStatus({ getUserList: false }));
  }
}

function* watchUserUpdate({ payload }: { payload: { data: Partial<IUserSlice>; image: FormData | undefined } }) {
  const { data } = payload;
  try {
    let isEmailAlreadyExists = false;
    if (data.email) {
      const res: AxiosResponse = yield call(validateEmail, `${data.email}`);
      isEmailAlreadyExists = res.data;
      yield put(authActions.setComponentStatus({ isEmailAlreadyExists }));
    }

    if (!isEmailAlreadyExists) {
      if (Object.values(_.omit(data, ['email'])).length > 0) {
        yield call(updateUser, _.omit(data, ['email']));
      }

      const componentStatus = data.email ? { emailVerifyModalOpen: true } : { successModalOpen: true };

      yield put(authActions.setComponentStatus(componentStatus));
    }
  } catch (err: any) {
    yield put(authActions.setComponentStatus({ error: err.response.data.message }));
  }
  yield put(authActions.setComponentStatus({ isSubmitting: false }));
}

function* watchUpdateUser(action: PayloadAction<UserUpdateFormData>) {
  try {
    yield put(userActions.updateUserStart());
    let renamedImage: File | null = null;
    const currentAvatar: string = yield select((state: RootState) => state.user.avatar);
    if (action.payload.file) {
      const fileExtension = action.payload.file?.name.split('.').pop();
      const timestamp = new Date().getTime();

      const imageName = `${timestamp}.${fileExtension}`;

      if (action.payload.file.name !== currentAvatar?.split('/').pop()) {
        renamedImage = new File([action.payload.file], imageName, { type: action.payload.file.type });
      }
    }
    const sanitizedAvatar = currentAvatar?.split('/').pop();

    const userUpdateData: UserUpdateData = {
      avatar: renamedImage ? renamedImage?.name : sanitizedAvatar,
      avatarContentType: renamedImage?.type,
      username: action.payload.username,
      firstName: action.payload.firstName,
      lastName: action.payload.lastName,
      email: action.payload.email,
      phone: action.payload.phone,
    };

    const response: AxiosResponse = yield call(updateUser, userUpdateData);

    if (renamedImage) {
      const { avatarPresignedUrl } = response.data;
      yield call(uploadImageToS3, avatarPresignedUrl, renamedImage);
      yield put(userActions.resetUpdateSuccess());
    }

    yield put(userActions.updateUserSuccess(response.data));
    yield put(userActions.setUser(response.data));
    toast.success('Profile Updated', toastStyles.success);
  } catch (error) {
    yield put(userActions.updateUserFailure('Update Failed'));
    toast.error('Profile Update Failure', toastStyles.error);
  }
}

function* watchSendEmailVerificationCode() {
  yield put(authActions.setComponentStatus({ isSubmitting: false }));
}

function* watchChangePassword({ payload }: { payload: { password: string; newPassword: string } }) {
  const { password, newPassword } = payload;
  try {
    yield put(userActions.setComponentStatus({ isSubmitting: true }));
    yield call(changePassword, password, newPassword);
    yield put(userActions.setComponentStatus({ successModalOpen: true }));
    toast.success('Password Updated!', toastStyles.success);
  } catch (err: any) {
    if (err.response && err.response.status === 403) {
      yield put(userActions.setComponentStatus({ isCurrentPasswordIncorrect: true }));
    } else if (err.response.data && err.response.data.errors) {
      yield put(userActions.setComponentStatus({ errorFields: err.response.data.errors }));
    } else {
      yield put(
        userActions.setComponentStatus({
          error: err.response?.data?.message || 'An unexpected error occurred.',
        }),
      );
      toast.error('Error updating password', toastStyles.error);
    }
  } finally {
    yield put(userActions.setComponentStatus({ isSubmitting: false }));
  }
}

function* watchCheckPassword(action: PayloadAction<{ password: string }>) {
  try {
    yield put(userActions.setComponentStatus({ isSubmitting: true }));
    yield call(checkPassword, action.payload.password);
    yield put(userActions.checkPasswordSuccess());
  } catch (error: any) {
    if (error.response && error.response.status === 403) {
      yield put(userActions.checkPasswordFailure('The current password is incorrect'));
    } else {
      const errorMessage =
        typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : 'An unexpected error occurred.';
      yield put(userActions.checkPasswordFailure(errorMessage));
      toast.error(errorMessage, toastStyles.error);
    }
  } finally {
    yield put(userActions.setComponentStatus({ isSubmitting: false }));
  }
}
function* watchSaveFeedback() {
  yield put(userActions.setComponentStatus({ isFeedbackSubmitting: true }));
  try {
    yield put(userActions.setComponentStatus({ isFeedbackSubmitting: false }));
    yield put(userActions.setComponentStatus({ isSendFeedbackSuccessModalOpen: true }));
  } catch (e) {
    yield put(userActions.setComponentStatus({ isFeedbackSubmitting: false }));
  }
}

function* watchGetDesignations() {
  try {
    yield put(authActions.setComponentStatus({ isSubmitting: true }));
    const result: AxiosResponse = yield call(getDesignations);
    yield put(authActions.setComponentStatus({ isSubmitting: false }));
    yield put(userActions.setDesignations(result.data));
  } catch (error: any) {
    yield put(authActions.setComponentStatus({ isSubmitting: false }));
  }
}

function* watchUserPinUpdate({ payload }: { payload: { pin: string | null } }) {
  try {
    yield put(userActions.setComponentStatus({ isPinSetting: true }));
    const res: AxiosResponse = yield call(createOrUpdatePin, payload.pin);
    if (res.status === 200) {
      yield put(userActions.setComponentStatus({ isPinSetting: false }));
      if (payload.pin !== null) {
        yield put(userActions.setComponentStatus({ successModalOpen: true }));
      }
      yield put(userActions.setUser({ isPinSkipped: res?.data?.isPinSkipped }));
    } else if (res.status === 304) {
      yield put(userActions.setComponentStatus({ isPinSetting: false }));
      yield put(userActions.setComponentStatus({ error: 'Pin Cannot Be Same As Previous' }));
    }
  } catch (error) {
    yield put(userActions.setComponentStatus({ isPinSetting: false }));
    yield put(userActions.setComponentStatus({ error: 'Error Creating a pin' }));
  }
}

function* watchChangePin({ payload }: { payload: { newPin: string } }) {
  try {
    yield put(userActions.setComponentStatus({ isPinSetting: true }));
    const res: AxiosResponse = yield call(updateUserPin, payload.newPin);
    if (res.status === 200) {
      yield put(userActions.setComponentStatus({ isPinSetting: false }));
      yield put(userActions.setComponentStatus({ successModalOpen: true }));
      toast.success('PIN Updated!', toastStyles.success);
    }
  } catch (error: any) {
    yield put(userActions.setComponentStatus({ isPinSetting: false }));
    yield put(userActions.setComponentStatus({ error: error.response?.data?.message || 'Error updating PIN' }));
    toast.error('Error updating PIN', toastStyles.error);
  }
}
export function* watchUserSagas(): Generator<ForkEffect, void> {
  yield debounce(1, userActions.getUserAsync, watchGetUser);
  yield takeLatest(userActions.getUserList, watchGetUserList);
  yield takeLatest(userActions.userUpdateAsync, watchUserUpdate);
  yield takeLatest(userActions.updateUserAsync, watchUpdateUser);
  yield takeLatest(userActions.sendEmailVerificationCodeAsync, watchSendEmailVerificationCode);
  yield takeLatest(userActions.changePasswordAsync, watchChangePassword);
  yield takeLatest(userActions.checkPasswordAsync, watchCheckPassword);
  yield takeLatest(userActions.saveFeedback, watchSaveFeedback);
  yield takeLatest(userActions.getDesignationsAsync, watchGetDesignations);
  yield takeLatest(userActions.updateUserPinAsync, watchUserPinUpdate);
  yield takeLatest(userActions.changePinAsync, watchChangePin);
}
const userSagas = watchUserSagas;
export default userSagas;
